import { useState, useEffect } from "react"
import { OpportunityStage } from "models/FullApplication"
import { EventAction, EventCategory } from "models/GoogleAnalytics"
import { LogGaEvent } from "lib/GoogleAnalytics"
import {
  StatusData,
  shouldFetchStatusData,
  fetchStatusFromApi,
  transformStatusData,
} from "../utils/statusApiUtils"

interface UseStatusDataProps {
  applicationId: string
  opportunityStage: OpportunityStage
}

interface UseStatusDataReturn {
  fetchedStatus: StatusData | null
  isLoading: boolean
  error: string | null
}

export const getStatus = async (
  applicationId: string,
  setFetchedStatus: (status: StatusData | null) => void,
  setIsLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) => {
  try {
    setIsLoading(true)
    setError(null)

    const data = await fetchStatusFromApi(applicationId)
    const status = transformStatusData(data)

    setFetchedStatus(status)

    LogGaEvent({
      action: EventAction.caseStatus,
      event_category: EventCategory.customEvent,
      event_label: `Recommendation accepted ${JSON.stringify(
        status.recommendationAccepted
      )}, documents requested: ${JSON.stringify(status.documentsRequested)}}`,
    })
  } catch (e) {
    console.log(e)
    setError("Failed to fetch status data")
  } finally {
    setIsLoading(false)
  }
}

export const useStatusData = ({
  applicationId,
  opportunityStage,
}: UseStatusDataProps): UseStatusDataReturn => {
  const [fetchedStatus, setFetchedStatus] = useState<StatusData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!applicationId) return

    if (shouldFetchStatusData(opportunityStage)) {
      getStatus(applicationId, setFetchedStatus, setIsLoading, setError)
    }
  }, [applicationId, opportunityStage])

  return {
    fetchedStatus,
    isLoading,
    error,
  }
}
